import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchReportitemledgers,
    createReportitemledger,
    updateReportitemledger,
    deleteReportitemledger,
    downloadReportitemledger,
    setPagination,
    setSort,
    searchReportitemledgers,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    setSelected,
    resetFiltersAndPagination,

    changeClassicFilter
} from '../../store/modules/reportitemledger/reportitemledgerActions';
import ReportitemledgerForm from '../../components/pageModule/Reportitemledgers/ReportitemledgerForm';

const MySwal = withReactContent(Swal);

const Reportitemledgers = ({
    excludedActions = [],
    onSelectedRowsChange = () => { },
    onClickedRow = () => { },
    externalCondition = {},
    selectedIds = []
}) => {

    const reportitemledgerState = useSelector(state => state.reportitemledger);
    const dispatch = useDispatch();

    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createReportitemledger(formData));
            toast.success('Reportitemledger created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating reportitemledger', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updateReportitemledger(id, formData));
            toast.success('Reportitemledger updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating reportitemledger', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteReportitemledger(id));
                toast.success('Reportitemledger deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting reportitemledger', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const handleDownload = async (type) => {
        try {
            const response = await dispatch(downloadReportitemledger(type));
            window.open(response.url, '_blank')
            return response;
        } catch (error) {
            toast.error(error.message || 'Error downloading reportitemledgers', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    };

    const columns = [
        {
            name: 'Date',
            selector: row => row.transaction_date,
        },
        {
            name: 'Transaction Type',
            selector: row => row.transaction_type,
        },
        {
            name: 'Item Name',
            selector: row => row.item_name?.en ?? '-',
        },
        {
            name: 'Item Code',
            selector: row => row.item_code,
        },
        {
            name: 'UOM',
            selector: row => row.measurement_unit_name?.en ?? '-',
        },
        {
            name: 'Quantity Issued',
            selector: row => row.qty_issued == 0 ? '' : row.qty_issued,
        },
        {
            name: 'Quantity Received',
            selector: row => row.qty_received == 0 ? '' : row.qty_received,
        }
    ];


    const actions = {
        fetch: (queryFilter, activeFilter, shouldUseFilter, externalCondition) => dispatch(fetchReportitemledgers(queryFilter, activeFilter, shouldUseFilter, externalCondition)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        download: handleDownload,
        search: (search) => dispatch(searchReportitemledgers(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
        setSelected: (selectedRows) => dispatch(setSelected(selectedRows)),

        onClassicFilterChange: (name, value) => dispatch(changeClassicFilter(name, value)),
    };

    const defaultAvailableActions = ['export_pdf'];
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    return (
        <>
            <GenericPage
                title="Report Item Ledger Summary"
                moduleName="Reportitemledger"
                moduleKey="reportitemledgers"
                state={reportitemledgerState}
                actions={actions}
                FormComponent={ReportitemledgerForm}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                selectableRows={false}
                onSelectedRowsChange={onSelectedRowsChange}
                onClickedRow={onClickedRow}
                selectedIds={selectedIds}
                externalCondition={externalCondition}
                noTable={true}
            />
        </>
    );
};

export default Reportitemledgers; 